import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { ChevronDown, ChevronUp, Trash2 } from 'lucide-react';

const fontOrbitron = { fontFamily: "'Orbitron', 'Arial', sans-serif" };
const fontRajdhani = { fontFamily: "'Rajdhani', 'Arial', sans-serif" };
const fontMono = { fontFamily: "'Share Tech Mono', monospace" };

const DELIVERY_PIPELINE = [
  { key: 'handoff', label: 'Handoff Received' },
  { key: 'scope', label: 'Scope Confirmed' },
  { key: 'in_progress', label: 'Work In Progress' },
  { key: 'review', label: 'Review' },
  { key: 'final', label: 'Final Delivery' },
  { key: 'completed', label: 'Completed' },
];

const mapToDeliveryStage = (stage) => {
  if (!stage) return 'handoff';
  if (stage === 'handed_off' || stage === 'onboarding') return 'handoff';
  if (stage === 'scope_confirmed') return 'scope';
  if (stage === 'in_progress') return 'in_progress';
  if (stage === 'completing' || stage === 'review') return 'review';
  if (stage === 'final_delivery') return 'final';
  if (stage === 'completed') return 'completed';
  return 'handoff';
};

const STAGE_ADVANCE = {
  handoff: 'scope_confirmed',
  scope: 'in_progress',
  in_progress: 'completing',
  review: 'final_delivery',
  final: 'completed',
  completed: null,
};

const getRiskStatus = (dateStr) => {
  if (!dateStr) return { label: 'UNKNOWN', color: '#6b7280', bg: 'rgba(107,114,128,0.1)' };
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days >= 7) return { label: 'AT RISK', color: '#E84040', bg: 'rgba(232,64,64,0.1)', days };
  if (days >= 3) return { label: 'NEEDS ATTENTION', color: '#EF9F27', bg: 'rgba(239,159,39,0.1)', days };
  return { label: 'ON TRACK', color: '#2DC896', bg: 'rgba(45,200,150,0.1)', days };
};

const formatDealValue = (val) => {
  if (!val && val !== 0) return '$0';
  return '$' + Number(val)?.toLocaleString('en-US');
};

const getDaysAgo = (dateStr) => {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr)?.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

const formatActivityTimestamp = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const month = months?.[d?.getMonth()];
  const day = d?.getDate();
  let hours = d?.getHours();
  const mins = String(d?.getMinutes())?.padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${month} ${day} Â· ${hours}:${mins} ${ampm}`;
};

const getDefaultActivityLog = (client) => {
  const created = client?.created_at || new Date().toISOString();
  const updated = client?.updated_at || created;
  return [
    { timestamp: created, action: 'Lead converted to client' },
    { timestamp: created, action: 'Sales handoff received' },
    { timestamp: updated, action: 'Delivery plan created' },
    { timestamp: updated, action: 'Next action assigned' },
  ];
};

const getNextCommandFromMission = (mission) => {
  if (!mission) return null;
  const r = mission.results;
  if (!r) return mission.user_text || null;
  if (r.next_command) return r.next_command;
  if (r.what_to_do_next) return r.what_to_do_next;
  if (r.next_action) return r.next_action;
  if (typeof r === 'object') {
    const steps = Object.values(r);
    const incomplete = steps.find(s => s && typeof s === 'object' && s.status !== 'complete' && s.action);
    if (incomplete?.action) return incomplete.action;
    const first = steps.find(s => s && typeof s === 'object' && s.action);
    if (first?.action) return first.action;
  }
  return mission.user_text || null;
};

const findLinkedMission = async (client, userId) => {
  const directId = client.operation_id || client.mission_id || client.source_operation_id || client.source_mission_id;
  if (directId) {
    const { data } = await supabase.from('missions').select('*').eq('id', directId).eq('user_id', userId).single();
    if (data) return { mission: data, linkType: 'direct' };
  }
  const { data: missions } = await supabase
    .from('missions').select('*').eq('user_id', userId)
    .order('created_at', { ascending: false }).limit(20);
  if (!missions?.length) return null;
  const clientName = (client.name || client.full_name || '').toLowerCase();
  const company = (client.company || '').toLowerCase();
  const clientTime = new Date(client.created_at || Date.now()).getTime();
  let best = null, bestScore = -1;
  for (const m of missions) {
    let score = 0;
    const mText = (m.user_text || '').toLowerCase();
    if (clientName && mText.includes(clientName)) score += 3;
    if (company && mText.includes(company)) score += 3;
    const daysDiff = Math.abs(new Date(m.created_at).getTime() - clientTime) / 86400000;
    if (daysDiff < 1) score += 2;
    else if (daysDiff < 3) score += 1;
    if (score > bestScore) { bestScore = score; best = m; }
  }
  if (best) return { mission: best, linkType: bestScore >= 3 ? 'matched' : 'suggested' };
  return { mission: missions[0], linkType: 'suggested' };
};

const getExecutionSteps = (mission) => {
  if (!mission) return [];
  const r = mission.results;
  if (!r || typeof r !== 'object') return [];
  if (Array.isArray(r.action_cards)) {
    return r.action_cards.map((c, i) => ({
      text: c.title || c.action || '',
      impact: c.impact || (i === 0 ? 'REVENUE' : i <= 2 ? 'GROWTH' : 'SUPPORT'),
      time: c.time || null,
      hasRealData: true,
    })).filter(s => s.text);
  }
  const steps = Object.values(r).filter(s => s && typeof s === 'object' && s.action);
  if (steps.length > 0) return steps.map((s, i) => ({ text: s.action, impact: i === 0 ? 'REVENUE' : i <= 2 ? 'GROWTH' : 'SUPPORT', time: null, hasRealData: true })).filter(s => s.text);
  if (mission.plan && Array.isArray(mission.plan.steps)) return mission.plan.steps.map((s, i) => ({ text: s.action, impact: i === 0 ? 'REVENUE' : i <= 2 ? 'GROWTH' : 'SUPPORT', time: null, hasRealData: true })).filter(s => s.text);
  return [];
};

const IMPACT_STYLE = {
  REVENUE: { color: '#EF9F27', bg: 'rgba(239,159,39,0.08)', border: 'rgba(239,159,39,0.25)', btnBg: 'rgba(239,159,39,0.1)', btnBorder: 'rgba(239,159,39,0.4)', btnColor: '#EF9F27' },
  GROWTH:  { color: '#b07dff', bg: 'rgba(124,63,255,0.08)', border: 'rgba(124,63,255,0.25)', btnBg: 'rgba(124,63,255,0.1)', btnBorder: 'rgba(124,63,255,0.4)', btnColor: '#b07dff' },
  SUPPORT: { color: '#2DC896', bg: 'rgba(45,200,150,0.08)', border: 'rgba(45,200,150,0.2)',  btnBg: 'rgba(45,200,150,0.06)', btnBorder: 'rgba(45,200,150,0.25)', btnColor: '#2DC896' },
};


const StatCard = ({ label, value, color }) => (
  <div style={{
    background: '#0a0b1a',
    border: '1px solid rgba(45,200,150,0.15)',
    borderRadius: '6px',
    padding: '16px 20px',
    flex: '1 1 140px',
    minWidth: '140px',
    boxSizing: 'border-box',
  }}>
    <div style={{ ...fontOrbitron, fontSize: '22px', fontWeight: 700, color: color || '#2DC896', lineHeight: 1 }}>
      {value}
    </div>
    <div style={{ ...fontRajdhani, fontSize: '12px', color: 'rgba(255,255,255,0.45)', marginTop: '6px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
      {label}
    </div>
  </div>
);

const ClientCard = ({ client, userId, allMissions, onDelete }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [linkedMission, setLinkedMission] = useState(null);
  const [missionLoading, setMissionLoading] = useState(true);
  const [confirmLink, setConfirmLink] = useState(false);
  const [missionPickerOpen, setMissionPickerOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const deliveryStage = mapToDeliveryStage(client?.stage);
  const riskDate = client?.updated_at || client?.created_at;
  const risk = getRiskStatus(riskDate);
  const daysAgo = getDaysAgo(riskDate);
  const dealValue = Number(client?.deal_value) || 0;
  const paidAmount = Number(client?.paid_amount) || 0;
  const remaining = dealValue - paidAmount;
  const rawLog = Array.isArray(client?.activity_log) ? client.activity_log : [];
  const activityLog = rawLog.length > 0 ? rawLog : getDefaultActivityLog(client);
  const currentStageIdx = DELIVERY_PIPELINE.findIndex(s => s.key === deliveryStage);

  useEffect(() => {
    const load = async () => {
      setMissionLoading(true);
      try {
        const result = await findLinkedMission(client, userId);
        setLinkedMission(result || null);
      } catch (err) {
        console.error('Mission link error:', err);
      } finally {
        setMissionLoading(false);
      }
    };
    if (userId) load();
    else setMissionLoading(false);
  }, [client.id, userId]);

  const nextCommand = getNextCommandFromMission(linkedMission?.mission);

  const handleConfirmLink = async () => {
    if (!linkedMission?.mission?.id) return;
    try {
      await supabase.from('clients').update({ operation_id: linkedMission.mission.id }).eq('id', client.id).eq('user_id', userId);
      setLinkedMission(prev => ({ ...prev, linkType: 'direct' }));
      setConfirmLink(false);
    } catch (err) {
      console.error('Confirm link error:', err);
    }
  };

  const handleExecuteNextStep = (customText) => {
    if (executing) return;
    setExecuting(true);
    const text = (typeof customText === "string" ? customText : null) || nextCommand || 'Continue delivery execution for ' + (client.name || client.company || 'this client');
    const nextStage = STAGE_ADVANCE[deliveryStage] || null;
    const logEntry = { timestamp: new Date().toISOString(), action: 'Redirected to mission execution' + (nextStage ? ' â€” stage advanced to ' + nextStage : '') };
    const updates = { updated_at: new Date().toISOString(), activity_log: [...activityLog, logEntry] };
    if (nextStage) updates.stage = nextStage;
    supabase.from('clients').update(updates).eq('id', client.id).eq('user_id', userId).then(() => {
      navigate('/command-center-ai-execution-interface', {
        state: {
          prefillMission: text,
          client_id: client.id,
          operation_id: linkedMission?.mission?.id || null,
          source: 'delivery_command_center',
        }
      });
    });
  };

  const handleDelete = async () => {
    if (!window.confirm('Archive this client? They will be removed from the active list.')) return;
    setDeleting(true);
    try {
      await supabase.from('clients').update({ stage: 'archived', updated_at: new Date().toISOString() }).eq('id', client.id).eq('user_id', userId);
      onDelete && onDelete(client.id);
    } catch (err) {
      console.error('Archive client error:', err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{
      border: `1px solid ${risk.label === 'AT RISK' ? 'rgba(232,64,64,0.18)' : 'rgba(45,200,150,0.08)'}`,
      borderRadius: 7,
      background: 'rgba(45,200,150,0.02)',
      overflow: 'hidden',
      marginBottom: 8,
      transition: 'border-color 0.2s',
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = risk.label === 'AT RISK' ? 'rgba(232,64,64,0.35)' : 'rgba(45,200,150,0.18)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = risk.label === 'AT RISK' ? 'rgba(232,64,64,0.18)' : 'rgba(45,200,150,0.08)'; }}
    >
      {/* TOP ROW */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', cursor: 'default', flexWrap: 'wrap' }}>
        <div style={{ width: 38, height: 38, borderRadius: '50%', border: `2px solid ${risk.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <span style={{ ...fontOrbitron, fontSize: 9, fontWeight: 700, color: risk.color }}>{deliveryStage.slice(0,2).toUpperCase()}</span>
        </div>
        <span style={{ ...fontMono, fontSize: 10, padding: '2px 8px', borderRadius: 0, textTransform: 'uppercase', letterSpacing: '1px', color: risk.color, background: risk.bg, flexShrink: 0 }}>
          {risk.label}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ ...fontMono, fontSize: 13, color: '#e8f4ff', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {client?.name || client?.full_name || 'â€”'}
            </span>
            {client?.company && <span style={{ ...fontMono, fontSize: 10, color: 'rgba(232,244,255,0.4)' }}>{client.company}</span>}
          </div>
          {daysAgo !== null && (
            <div style={{ ...fontMono, fontSize: 9, color: daysAgo > 7 ? 'rgba(232,64,64,0.6)' : 'rgba(232,244,255,0.3)', marginTop: 2 }}>
              {daysAgo === 0 ? 'Updated today' : `${daysAgo}d ago`}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <div style={{ ...fontMono, fontSize: 12, color: '#2DC896' }}>{formatDealValue(dealValue)}</div>
        <div style={{ ...fontMono, fontSize: 9, padding: '3px 8px', borderRadius: 3, background: 'rgba(45,200,150,0.08)', border: '1px solid rgba(45,200,150,0.2)', color: '#2DC896', flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {deliveryStage.replace(/_/g, ' ').toUpperCase()}
        </div>
        <button
          onClick={handleDelete}
          disabled={deleting}
          style={{ background: 'transparent', border: '1px solid rgba(231,76,60,0.2)', borderRadius: 4, padding: '6px', cursor: deleting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s' }}
          onMouseEnter={e => { if (!deleting) { e.currentTarget.style.background = 'rgba(231,76,60,0.08)'; e.currentTarget.style.borderColor = 'rgba(231,76,60,0.45)'; }}}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(231,76,60,0.2)'; }}
        >
          <Trash2 style={{ width: 13, height: 13, color: '#e74c3c' }} />
        </button>
        {expanded ? <ChevronUp style={{ width: 14, height: 14, color: 'rgba(45,200,150,0.6)', flexShrink: 0, cursor: 'pointer' }} onClick={() => setExpanded(false)} /> : <ChevronDown style={{ width: 14, height: 14, color: 'rgba(45,200,150,0.6)', flexShrink: 0, cursor: 'pointer' }} onClick={() => setExpanded(true)} />}
        </div>
      </div>

      {expanded && (
      <div style={{ padding: '0 16px 14px', borderTop: '1px solid rgba(45,200,150,0.06)' }}>

      {/* Money row */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 12, marginBottom: 12 }}>
        <span style={{ ...fontMono, fontSize: 9, color: 'rgba(232,244,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Deal Value</span>
        <span style={{ ...fontOrbitron, fontSize: 15, fontWeight: 700, color: '#EF9F27' }}>{formatDealValue(dealValue)}</span>
      </div>

      {/* Linked Operation */}
      <div style={{ marginBottom: 10, background: 'rgba(83,74,183,0.05)', border: '1px solid rgba(83,74,183,0.2)', borderRadius: '6px', padding: '10px 14px' }}>
        <div style={{ ...fontMono, fontSize: '8px', color: 'rgba(83,74,183,0.7)', letterSpacing: '2px', marginBottom: '5px', textTransform: 'uppercase' }}>LINKED OPERATION</div>
        {missionLoading ? (
          <div style={{ ...fontRajdhani, fontSize: '13px', color: 'rgba(255,255,255,0.3)' }}>Loading...</div>
        ) : linkedMission?.mission ? (
          <div>
            <div style={{ ...fontRajdhani, fontSize: '13px', color: '#fff', fontWeight: 600, marginBottom: '4px' }}>
              {linkedMission.mission.user_text?.slice(0, 80) || 'Untitled operation'}
              {linkedMission.mission.user_text?.length > 80 ? '...' : ''}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ ...fontMono, fontSize: '8px', padding: '2px 6px', borderRadius: '3px',
                background: linkedMission.linkType === 'direct' ? 'rgba(45,200,150,0.1)' : 'rgba(239,159,39,0.1)',
                color: linkedMission.linkType === 'direct' ? '#2DC896' : '#EF9F27',
                border: `1px solid ${linkedMission.linkType === 'direct' ? 'rgba(45,200,150,0.3)' : 'rgba(239,159,39,0.3)'}`,
                textTransform: 'uppercase', letterSpacing: '1px' }}>
                {linkedMission.linkType === 'direct' ? 'LINKED' : 'SUGGESTED'}
              </span>
              {linkedMission.linkType !== 'direct' && !confirmLink && (
                <button onClick={() => setConfirmLink(true)}
                  style={{ ...fontMono, fontSize: '8px', background: 'transparent', border: '1px solid rgba(45,200,150,0.3)', borderRadius: '3px', padding: '2px 8px', color: '#2DC896', cursor: 'pointer', letterSpacing: '1px' }}>
                  USE THIS OPERATION
                </button>
              )}
              {confirmLink && (
                <button onClick={handleConfirmLink}
                  style={{ ...fontMono, fontSize: '8px', background: 'rgba(45,200,150,0.1)', border: '1px solid rgba(45,200,150,0.4)', borderRadius: '3px', padding: '2px 8px', color: '#2DC896', cursor: 'pointer', letterSpacing: '1px' }}>
                  CONFIRM LINK
                </button>
              )}
              <button onClick={() => setMissionPickerOpen(v => !v)}
                style={{ ...fontMono, fontSize: '8px', background: 'transparent', border: '1px solid rgba(83,74,183,0.4)', borderRadius: '3px', padding: '2px 8px', color: '#8B83E8', cursor: 'pointer', letterSpacing: '1px' }}>
                {missionPickerOpen ? 'CLOSE' : 'CHOOSE OPERATION'}
              </button>
            </div>
            {missionPickerOpen && allMissions?.length > 0 && (
              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '180px', overflowY: 'auto' }}>
                {allMissions.map((m) => (
                  <button key={m.id} onClick={async () => {
                    await supabase.from('clients').update({ operation_id: m.id }).eq('id', client.id).eq('user_id', userId);
                    setLinkedMission({ mission: m, linkType: 'direct' });
                    setMissionPickerOpen(false);
                  }}
                    style={{ textAlign: 'left', background: linkedMission?.mission?.id === m.id ? 'rgba(45,200,150,0.08)' : 'rgba(255,255,255,0.02)', border: `1px solid ${linkedMission?.mission?.id === m.id ? 'rgba(45,200,150,0.3)' : 'rgba(255,255,255,0.08)'}`, borderRadius: '4px', padding: '6px 10px', cursor: 'pointer', ...fontRajdhani, fontSize: '12px', color: '#fff' }}>
                    {(m.user_text || 'Untitled').slice(0, 70)}{(m.user_text || '').length > 70 ? '...' : ''}
                    <span style={{ ...fontMono, fontSize: '8px', color: 'rgba(255,255,255,0.3)', marginLeft: '8px' }}>{new Date(m.created_at).toLocaleDateString()}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            <div style={{ ...fontRajdhani, fontSize: '13px', color: 'rgba(255,255,255,0.35)', marginBottom: '6px' }}>No operation linked yet</div>
            <button onClick={() => setMissionPickerOpen(v => !v)}
              style={{ ...fontMono, fontSize: '8px', background: 'transparent', border: '1px solid rgba(83,74,183,0.4)', borderRadius: '3px', padding: '2px 8px', color: '#8B83E8', cursor: 'pointer', letterSpacing: '1px' }}>
              {missionPickerOpen ? 'CLOSE' : 'CHOOSE OPERATION'}
            </button>
            {missionPickerOpen && allMissions?.length > 0 && (
              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '180px', overflowY: 'auto' }}>
                {allMissions.map((m) => (
                  <button key={m.id} onClick={async () => {
                    await supabase.from('clients').update({ operation_id: m.id }).eq('id', client.id).eq('user_id', userId);
                    setLinkedMission({ mission: m, linkType: 'direct' });
                    setMissionPickerOpen(false);
                  }}
                    style={{ textAlign: 'left', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '4px', padding: '6px 10px', cursor: 'pointer', ...fontRajdhani, fontSize: '12px', color: '#fff' }}>
                    {(m.user_text || 'Untitled').slice(0, 70)}{(m.user_text || '').length > 70 ? '...' : ''}
                    <span style={{ ...fontMono, fontSize: '8px', color: 'rgba(255,255,255,0.3)', marginLeft: '8px' }}>{new Date(m.created_at).toLocaleDateString()}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {/* Execution Pipeline */}
      <div style={{ marginTop: 10, marginBottom: 10 }}>
        <div style={{ ...fontMono, fontSize: '8px', color: 'rgba(45,200,150,0.5)', letterSpacing: '2px', marginBottom: 8, textTransform: 'uppercase' }}>EXECUTION PIPELINE</div>
        {missionLoading ? (
          <div style={{ ...fontRajdhani, fontSize: '12px', color: 'rgba(255,255,255,0.3)' }}>Loading...</div>
        ) : getExecutionSteps(linkedMission?.mission).length === 0 ? (
          <div style={{ ...fontRajdhani, fontSize: '12px', color: 'rgba(255,255,255,0.3)' }}>No steps available. Link an operation above.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {getExecutionSteps(linkedMission?.mission).map((step, i) => {
              const stepText = typeof step === 'object' ? step.text : step;
              const imp = (step && step.hasRealData) ? (IMPACT_STYLE[step.impact] || IMPACT_STYLE.SUPPORT) : null;
              const defaultImp = IMPACT_STYLE.SUPPORT;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'rgba(45,200,150,0.02)', border: '1px solid rgba(45,200,150,0.07)', borderRadius: 4 }}>
                  <span style={{ ...fontMono, fontSize: 9, color: 'rgba(45,200,150,0.4)', flexShrink: 0, minWidth: 18 }}>{String(i + 1).padStart(2, '0')}</span>
                  <span style={{ ...fontRajdhani, fontSize: 12, color: 'rgba(232,244,255,0.7)', flex: 1, lineHeight: 1.4 }}>{stepText}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    {imp && (
                      <span style={{ ...fontMono, fontSize: 8, fontWeight: 700, color: imp.color, background: imp.bg, border: `1px solid ${imp.border}`, borderRadius: 3, padding: '2px 7px', letterSpacing: '1px', textTransform: 'uppercase' }}>
                        {step.impact}
                      </span>
                    )}
                    {step && step.time && (
                      <span style={{ ...fontMono, fontSize: 8, color: 'rgba(232,244,255,0.3)' }}>{step.time}</span>
                    )}
                    <button
                      onClick={() => handleExecuteNextStep(stepText)}
                      style={{ background: imp ? imp.btnBg : defaultImp.btnBg, border: `1px solid ${imp ? imp.btnBorder : defaultImp.btnBorder}`, borderRadius: 3, padding: '3px 10px', ...fontMono, fontSize: 8, color: imp ? imp.btnColor : defaultImp.btnColor, cursor: 'pointer', letterSpacing: '1px', textTransform: 'uppercase' }}
                      onMouseEnter={e => { e.currentTarget.style.opacity = '0.8'; }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                    >
                      EXECUTE
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>


      {/* Next Delivery Action */}
      <div style={{ marginTop: 10, background: 'rgba(45,200,150,0.04)', border: '1px solid rgba(45,200,150,0.15)', borderRadius: '6px', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...fontMono, fontSize: '8px', color: 'rgba(45,200,150,0.6)', letterSpacing: '2px', marginBottom: '5px', textTransform: 'uppercase' }}>NEXT STEP</div>
          <div style={{ ...fontRajdhani, fontSize: '14px', color: '#fff', fontWeight: 600 }}>
            {missionLoading ? 'Loading operation...' : nextCommand ? nextCommand.slice(0, 120) + (nextCommand.length > 120 ? '...' : '') : 'Define scope'}
          </div>
        </div>
        <button
          onClick={handleExecuteNextStep}
          disabled={executing || deliveryStage === 'completed'}
          style={{
            background: executing ? 'rgba(45,200,150,0.06)' : 'rgba(45,200,150,0.12)',
            border: '1px solid rgba(45,200,150,0.4)',
            borderRadius: '4px',
            color: '#2DC896',
            ...fontOrbitron,
            fontSize: '9px',
            fontWeight: 700,
            letterSpacing: '0.1em',
            padding: '8px 16px',
            cursor: executing || deliveryStage === 'completed' ? 'not-allowed' : 'pointer',
            textTransform: 'uppercase',
            flexShrink: 0,
            boxShadow: executing ? 'none' : '0 0 10px rgba(45,200,150,0.5), 0 0 20px rgba(45,200,150,0.25)',
            transition: 'all 0.2s',
          }}
          onMouseEnter={e => { if (!executing) { e.currentTarget.style.boxShadow = '0 0 16px rgba(45,200,150,0.7), 0 0 32px rgba(45,200,150,0.4)'; }}}
          onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 0 10px rgba(45,200,150,0.5), 0 0 20px rgba(45,200,150,0.25)'; }}
        >
          {executing ? 'REDIRECTING...' : 'EXECUTE NEXT STEP'}
        </button>
      </div>

      {/* Activity Log Toggle */}
      <div style={{ marginTop: 8, paddingLeft: 4 }}>
        <button
          onClick={() => setActivityOpen(v => !v)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', ...fontMono, fontSize: 9, color: 'rgba(45,200,150,0.6)', letterSpacing: '0.1em', transition: 'color 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.color = '#2DC896'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'rgba(45,200,150,0.6)'; }}
        >
          {activityOpen ? 'HIDE ACTIVITY LOG' : 'VIEW ACTIVITY LOG'}
        </button>
        {activityOpen && (
          <div style={{ marginTop: 8, background: 'rgba(45,200,150,0.02)', border: '1px solid rgba(45,200,150,0.08)', borderRadius: 6, padding: '10px 14px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {activityLog.map((entry, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#2DC896', flexShrink: 0, marginTop: 3 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ ...fontMono, fontSize: 9, color: 'rgba(232,244,255,0.3)', letterSpacing: '0.06em' }}>{formatActivityTimestamp(entry?.timestamp || entry?.created_at)}</span>
                    <div style={{ ...fontMono, fontSize: 11, color: '#b8d4c8', marginTop: 2, lineHeight: 1.5 }}>{entry?.action || entry?.text || entry?.note || JSON.stringify(entry)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      </div>
      )}
    </div>
  );
};

const DeliveryCommandCenter = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [allMissions, setAllMissions] = useState([]);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const uid = sessionData?.session?.user?.id;
        if (!uid) { setLoading(false); return; }
        setUserId(uid);
        const { data, error } = await supabase
          .from('clients')
          .select('*')
          .eq('user_id', uid)
          .order('updated_at', { ascending: false });
        if (error) { console.error('Error fetching clients:', error); }
        else { setClients((data || []).filter(c => c.stage !== 'archived')); }
        const { data: missions } = await supabase.from('missions').select('id, user_text, created_at, results').eq('user_id', uid).order('created_at', { ascending: false }).limit(30);
        if (missions) setAllMissions(missions);
      } catch (err) {
        console.error('Clients fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, []);

  const activeClients = clients.filter(c => c?.stage !== 'completed');
  const activeDeliveryValue = activeClients.reduce((sum, c) => sum + (Number(c?.deal_value) || 0), 0);
  const clientsInDelivery = activeClients.length;
  const atRiskValue = clients
    .filter(c => getRiskStatus(c?.updated_at || c?.created_at).label === 'AT RISK')
    .reduce((sum, c) => sum + (Number(c?.deal_value) || 0), 0);
  const completedRevenue = clients
    .filter(c => c?.stage === 'completed')
    .reduce((sum, c) => sum + (Number(c?.deal_value) || 0), 0);

  return (
    <div style={{ minHeight: '100vh', background: '#05060f', padding: 'clamp(16px, 4vw, 40px) clamp(12px, 3vw, 24px)', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ marginBottom: '32px' }}>
          <div style={{ fontFamily: "'Orbitron','Arial',sans-serif", fontSize: '11px', color: '#2DC896', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '6px', fontWeight: 700 }}>RECALLCORE CRM</div>
          <h1 style={{ fontFamily: "'Orbitron','Arial',sans-serif", fontSize: '28px', fontWeight: 900, color: '#fff', letterSpacing: '0.06em', margin: 0, textTransform: 'uppercase' }}>
            DELIVERY COMMAND CENTER
          </h1>
          <p style={{ fontFamily: "'Rajdhani','Arial',sans-serif", fontSize: '14px', color: 'rgba(255,255,255,0.4)', marginTop: '6px', letterSpacing: '0.04em' }}>
            {clients.length} client{clients.length !== 1 ? 's' : ''} in delivery
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '32px', flexWrap: 'wrap' }}>
          <StatCard label="Active Delivery Value" value={loading ? '' : formatDealValue(activeDeliveryValue)} color="#2DC896" />
          <StatCard label="Clients in Delivery" value={loading ? '' : clientsInDelivery} color="#2DC896" />
          <StatCard label="At Risk Value" value={loading ? '' : formatDealValue(atRiskValue)} color="#E84040" />
          <StatCard label="Completed Revenue" value={loading ? '' : formatDealValue(completedRevenue)} color="#534AB7" />
        </div>
        {loading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[1, 2, 3].map(i => (
              <div key={i} style={{ background: '#0a0b1a', border: '1px solid rgba(45,200,150,0.1)', borderRadius: '8px', height: '160px', opacity: 0, pointerEvents: 'none' }} />
            ))}
          </div>
        )}
        {!loading && clients.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px', textAlign: 'center' }}>
            <p style={{ fontFamily: "'Rajdhani','Arial',sans-serif", fontSize: '16px', color: 'rgba(255,255,255,0.4)', maxWidth: '420px', lineHeight: 1.6, marginBottom: '24px' }}>
              No active clients. Close a lead in the Sales Crew to begin delivery operations.
            </p>
            <button
              onClick={() => navigate('/lead-command-center')}
              style={{ background: '#2DC896', color: '#000', border: 'none', borderRadius: '4px', fontFamily: "'Orbitron','Arial',sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', padding: '12px 28px', cursor: 'pointer', textTransform: 'uppercase' }}
            >
              GO TO SALES CREW
            </button>
          </div>
        )}
        {!loading && clients.length > 0 && (
          <div>
            {clients.map((client) => (
              <ClientCard key={client.id} client={client} userId={userId} allMissions={allMissions} onDelete={(id) => setClients(prev => prev.filter(c => c.id !== id))} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryCommandCenter;
